import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API } from 'src/environments/environment';
import {
  criarFiltroRegistrosPadrao,
  FiltroDefinidoCondicao,
  FiltroDefinidoRegistros,
  FiltroOperador,
  FiltroConector,
} from 'src/app/utils/filtro-definido-condicoes.util';
import { AjaxService } from './ajax.service';

/** Item selecionado na tela de filtros (ordem importa). */
export interface FiltroDefinidoCampo {
  abaId: string;
  abaTitulo: string;
  key: string;
  label: string;
}

/** Preset retornado pela API / uso na UI. */
export interface FiltroDefinidoSalvo {
  id: string;
  titulo: string;
  criadoEm: string;
  itens: FiltroDefinidoCampo[];
  filtroRegistros: FiltroDefinidoRegistros;
}

@Injectable({ providedIn: 'root' })
export class FiltrosDefinidosStorageService {
  constructor(private readonly ajax: AjaxService) {}

  /** Lista filtros da empresa (tabela `FILTRO_DEFINIDO_EMPRESA`). */
  listar(): Observable<FiltroDefinidoSalvo[]> {
    const url = `${API}empresa/FiltroDefinidoEmpresa/Listar`;
    return this.ajax.get(url).pipe(
      map((response) => this.normalizarListaResposta(response)),
      catchError(() => of([]))
    );
  }

  salvar(dados: {
    titulo: string;
    itens: FiltroDefinidoCampo[];
    filtroRegistros?: FiltroDefinidoRegistros;
    /** ID numérico ao editar (opcional). */
    id?: string;
  }): Observable<FiltroDefinidoSalvo> {
    const url = `${API}empresa/FiltroDefinidoEmpresa/Salvar`;
    const idNum = dados.id ? Number(dados.id) : 0;
    const filtroRegistros = dados.filtroRegistros ?? criarFiltroRegistrosPadrao();
    const payload: Record<string, unknown> = {
      titulo: (dados.titulo || '').trim(),
      itens: dados.itens.map((i) => ({
        abaId: i.abaId,
        abaTitulo: i.abaTitulo,
        key: i.key,
        label: i.label,
      })),
      filtroRegistros: {
        mostrarTodos: !!filtroRegistros.mostrarTodos,
        condicoes: (filtroRegistros.condicoes || []).map((c) => ({
          abaId: c.abaId,
          key: c.key,
          operador: c.operador,
          valor: c.valor,
          conector: c.conector,
        })),
      },
    };
    if (idNum > 0) {
      payload['id'] = idNum;
    }
    return this.ajax.post(url, payload).pipe(
      map((response) => {
        const msgFalha = this.mensagemSeErroApi(
          response,
          'Não foi possível salvar o filtro.'
        );
        if (msgFalha) {
          throw new Error(msgFalha);
        }
        const raw = this.desempacotarPayload(response);
        return this.mapearSalvo(raw);
      })
    );
  }

  excluir(id: string): Observable<void> {
    const n = Number(id);
    if (!n || n <= 0) {
      return of(undefined);
    }
    const url = `${API}empresa/FiltroDefinidoEmpresa/Excluir?id=${encodeURIComponent(String(n))}`;
    return this.ajax.delete(url).pipe(
      map((response) => {
        const msgFalha = this.mensagemSeErroApi(
          response,
          'Não foi possível excluir o filtro.'
        );
        if (msgFalha) {
          throw new Error(msgFalha);
        }
      }),
      map(() => undefined)
    );
  }

  private normalizarListaResposta(response: any): FiltroDefinidoSalvo[] {
    const payload = this.desempacotarPayload(response);
    const lista = Array.isArray(payload) ? payload : [];
    return lista
      .filter((x) => x && typeof x === 'object')
      .map((x) => this.mapearSalvo(x))
      .filter((x) => x.id.length > 0);
  }

  private mapearSalvo(raw: any): FiltroDefinidoSalvo {
    const idVal = raw?.id ?? raw?.Id;
    const itensRaw = raw?.itens ?? raw?.Itens ?? [];
    const itens: FiltroDefinidoCampo[] = Array.isArray(itensRaw)
      ? itensRaw.map((item: any) => ({
          abaId: String(item?.abaId ?? item?.AbaId ?? ''),
          abaTitulo: String(item?.abaTitulo ?? item?.AbaTitulo ?? ''),
          key: String(item?.key ?? item?.Key ?? ''),
          label: String(item?.label ?? item?.Label ?? ''),
        }))
      : [];
    return {
      id: String(idVal ?? ''),
      titulo: String(raw?.titulo ?? raw?.Titulo ?? ''),
      criadoEm: String(raw?.criadoEm ?? raw?.CriadoEm ?? ''),
      itens,
      filtroRegistros: this.mapearFiltroRegistros(raw),
    };
  }

  private mapearFiltroRegistros(raw: any): FiltroDefinidoRegistros {
    const fr = raw?.filtroRegistros ?? raw?.FiltroRegistros;
    if (!fr || typeof fr !== 'object') {
      return criarFiltroRegistrosPadrao();
    }
    const condicoesRaw = fr?.condicoes ?? fr?.Condicoes ?? [];
    const condicoes: FiltroDefinidoCondicao[] = Array.isArray(condicoesRaw)
      ? condicoesRaw.map((c: any) => ({
          abaId: String(c?.abaId ?? c?.AbaId ?? ''),
          key: String(c?.key ?? c?.Key ?? ''),
          operador: this.normalizarOperador(c?.operador ?? c?.Operador),
          valor: String(c?.valor ?? c?.Valor ?? ''),
          conector: this.normalizarConector(c?.conector ?? c?.Conector),
        }))
      : [];
    return {
      mostrarTodos: fr?.mostrarTodos ?? fr?.MostrarTodos ?? true,
      condicoes,
    };
  }

  private normalizarOperador(valor: unknown): FiltroOperador {
    const op = String(valor ?? 'igual').trim().toLowerCase();
    const validos: FiltroOperador[] = [
      'igual',
      'diferente',
      'contem',
      'comeca_com',
      'termina_com',
      'maior',
      'maior_igual',
      'menor',
      'menor_igual',
      'vazio',
      'nao_vazio',
    ];
    return validos.includes(op as FiltroOperador) ? (op as FiltroOperador) : 'igual';
  }

  private normalizarConector(valor: unknown): FiltroConector {
    const c = String(valor ?? 'e').trim().toLowerCase();
    return c === 'ou' ? 'ou' : 'e';
  }

  /** Envelope de erro comum na API (camelCase / PascalCase). */
  private mensagemSeErroApi(resposta: unknown, mensagemPadrao: string): string | null {
    if (resposta == null || typeof resposta !== 'object') {
      return null;
    }
    const r = resposta as Record<string, unknown>;
    const falha = Boolean(r['hasErrors'] || r['HasErrors']);
    if (!falha) {
      return null;
    }
    const m = r['message'] ?? r['Message'];
    return typeof m === 'string' && m.trim().length > 0 ? m : mensagemPadrao;
  }

  private desempacotarPayload(response: any): any {
    let atual: any = response;
    for (let i = 0; i < 4; i++) {
      if (
        atual &&
        typeof atual === 'object' &&
        Object.prototype.hasOwnProperty.call(atual, 'data') &&
        atual.data !== null &&
        atual.data !== undefined
      ) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return atual;
  }
}
